import { useEffect } from "react";
import { setMasteryThreshold } from "../../constants/MasteryConstants";
import { useApp } from "../../context/AppContextProvider";
import { useAuth } from "../../context/UserContextProvider";
import {
  retrieveLearnerCatchtivityBootstrap,
  queryStandardExamApplicationListBySeasonIdAndGradeIdAndBranchIdAndCurriculumType,
} from "../../requests/CatchtivityRequests";
import {
  queryLearnerAnnouncementFeed,
} from "../../requests/NotificationRequests";
import { querySubjectDescriptionListByCategoryIdList } from "../../requests/CategoryRequests";
import {
  buildOutcomeReportMapByCoterie,
  buildReportDetailDTOFromPerformance,
} from "../../utilization/ReportDetailBuilder";
import { isStaffSentPersonalizedAssignment } from "../../utilization/PersonalizedAssignmentTriggerUtilization";
import { normalizeSupportAssignmentContent } from "../../utilization/SupportAssignmentUtilization";
import {
  initializeTaskItemProgressDTO,
  queryTaskItemProgressDTOListByParams,
  retrieveLearnerGamificationBootstrap,
  retrieveLearnerPointsStatus,
  retrieveAchievementCatalog,
} from "../../requests/GamificationRequests";
import {
  setAchievementRuleCatalog,
  hasAchievementRuleCatalog,
  constructCategoryTreeFromParentCode,
  retrieveCategoryVersionCodeOptionList,
  retrieveSkillVersionCodeOptionList,
  calculateEndDateOfWeek,
  calculateStartDateOfWeek,
  filterCategoryVersionCodeOptionList,
} from "../../library";
import { queryLearnerFrameworkAnnualPlanListByParams } from "../../requests/PlanRequests";
import TaskProgressIndicator from "../../components/common/indicators/TaskProgressIndicator";
import categoryVersionCodeMEBList from "../../data/category-version-code-meb-list.json";
import categoryVersionCodeAPList from "../../data/category-version-code-ap-list.json";
import categoryVersionCodeIBList from "../../data/category-version-code-ib-list.json";
import categoryVersionCodeDFEList from "../../data/category-version-code-dfe-list.json";
import skillVersionCodeMEBList from "../../data/skill-version-code-meb-list.json";
import skillVersionCodeIBList from "../../data/skill-version-code-ib-list.json";
import { useLegacyHeroBannerMigration } from "../../hooks/useHeroBanner";

// Top-N rows requested per leaderboard list from the gamification bootstrap / points-status
// aggregates. The learner's own row (wherever it ranks) and the per-branch aggregate standings
// ride along server-side, so the UI never needs the full season list.
const RANKING_LIMIT = 50;

const LearnerApp = (props) => {
  const { boarding } = useApp();
  const {
    language,
    userInformation,
    userProfile,
    webSocketStompClient,
    userProfileGrade,
    userProfileBranch,
    userProfileSeason,
    skillDTOListAsTree,
    learnerDataRefreshCounter,
    setCategoryDTOList,
    setCategoryDTOListAsTree,
    setSkillDTOList,
    setSkillDTOListAsTree,
    branchAssignmentDTOList,
    supportAssignmentDTOList,
    standardExamApplicationDTOList,
    personalizedAssignmentDTOList,
    branchAssignmentReportDetailDTOList,
    taskItemProgressDTOList,
    setBranchAssignmentDTOList,
    setBranchAssignmentListLoading,
    setSupportAssignmentDTOList,
    setSupportAssignmentListLoading,
    setStandardExamApplicationDTOList,
    setStandardExamApplicationListLoading,
    setActiveContentList,
    setActivePersonalizedAssignmentList,
    setPassivePersonalizedAssignmentList,
    setSubjectDescriptionListLoading,
    setSubjectDescriptionDTOList,
    setBranchAssignmentPerformanceLoading,
    setBranchAssignmentPerformance,
    setLearnerHandwrittenExamPerformanceLoading,
    setLearnerHandwrittenExamPerformance,
    setLearnerMockExamPerformanceLoading,
    setLearnerMockExamPerformance,
    setLearnerSupportAssignmentPerformanceLoading,
    setLearnerSupportAssignmentPerformance,
    setPersonalizedAssignmentPerformanceLoading,
    setPersonalizedAssignmentPerformance,
    setPersonalStatus,
    setWeeklyStreak,
    setNextAchievementProgress,
    setSummedActionCounts,
    setLearnerGoalProgressList,
    setBranchAssignmentReportDetailListLoading,
    setBranchAssignmentReportDetailDTOList,
    setBranchAssignmentCategoryScoreList,
    setLearnerCompletionOutcomeReportMap,
    setLearnerHandwrittenOutcomeReportMap,
    setLearnerMockExamOutcomeReportMap,
    setLearnerSupportAssignmentOutcomeReportMap,
    setReportCategoryDTOList,
    setNumberOfUnreadAnnouncements,
    setContestListLoading,
    setContestDTOList,
    setThisWeekRankingDTOList,
    setThisWeekRankingListLoading,
    setAllTimeRankingDTOList,
    setAllTimeRankingListLoading,
    setTaskItemProgressDTOList,
    setTaskItemProgressListLoading,
    setEarnedAchievementDTOList,
    setEarnedAchievementListLoading,
    setAnnouncementDTOList,
    setAnnouncementListLoading,
    setAvailableFrameworkAnnualPlanList,
    setAvailableFrameworkAnnualPlanListLoading,
    availableFrameworkAnnualPlanList,
    setFrameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap,
    setPersonalizedAssignmentDTOList,
    setPersonalizedAssignmentListLoading,
    setMyWrongActivityDTOList,
    setMyWrongActivityListLoading,
    setActivityReviewDTOList,
    setActivityReviewListLoading,
    setMarketItemPurchaseDTOList,
    setMarketItemPurchaseListLoading,
    setMascotInventoryItemDTOList,
    setMascotInventoryItemListLoading,
    setSelfThisWeekRanking,
    setSelfAllTimeRanking,
    setThisWeekBranchRankingDTOList,
    setAllTimeBranchRankingDTOList,
  } = useAuth();
  // Moves a legacy, browser-only profile background to the server once.
  useLegacyHeroBannerMigration();


  useEffect(() => {
    const retrieveAndSetLearnerCatchtivityBootstrap = async () => {
      setBranchAssignmentListLoading(true);
      setBranchAssignmentPerformanceLoading(true);
      setLearnerHandwrittenExamPerformanceLoading(true);
      setLearnerMockExamPerformanceLoading(true);
      setLearnerSupportAssignmentPerformanceLoading(true);
      setPersonalizedAssignmentPerformanceLoading(true);
      setBranchAssignmentReportDetailListLoading(true);
      // Contests + support assignments are now bundled into this one bootstrap call (was two extra parallel
      // catchtivity requests). Their loading flags are driven from here too.
      setContestListLoading(true);
      setSupportAssignmentListLoading(true);
      // Personalized assignments + wrong activities + activity reviews are bundled too (was three
      // parallel App.tsx requests at boot; identity pinned server-side on every sub-query).
      setPersonalizedAssignmentListLoading(true);
      setMyWrongActivityListLoading(true);
      setActivityReviewListLoading(true);
      const perfStart = performance.now();
      const { data, err } = await retrieveLearnerCatchtivityBootstrap({
        userId: userInformation.id,
        userProfileId: userProfile.id,
        seasonId: userProfileSeason.id,
        gradeId: userProfileGrade.id,
        branchId: userProfileBranch.id,
        curriculumType: userProfileGrade.curriculumType,
        // Weekly contest sub-query (identity pinned server-side); mirrors the old queryContestListByParams.
        contestParams: {
          coterieTypeList: ["GENERAL_CULTURE", "TURKISH"],
          level: userProfileGrade.level,
          type: "QUIZ",
          frequencyType: "WEEKLY",
          beginAt: calculateStartDateOfWeek(new Date(), true).getTime(),
          endAt: calculateEndDateOfWeek(new Date(), true).getTime(),
        },
        // Active support-assignment sub-query (studentUserProfileId + activeOnly forced server-side).
        supportAssignmentParams: {
          branchId: userProfileBranch?.id,
          deleted: false,
        },
        // The three former App.tsx boot requests, as bootstrap sub-queries.
        personalizedAssignmentParams: {
          userId: userInformation.id,
          userProfileId: userProfile.id,
        },
        wrongActivityParams: {
          userId: userInformation.id,
          userProfileId: userProfile.id,
          score: 1,
        },
        activityReviewParams: {
          userId: userInformation.id,
          userProfileId: userProfile.id,
        },
      });
      if (err) {
        setBranchAssignmentListLoading(false);
        setBranchAssignmentPerformanceLoading(false);
        setLearnerHandwrittenExamPerformanceLoading(false);
        setLearnerMockExamPerformanceLoading(false);
        setLearnerSupportAssignmentPerformanceLoading(false);
        setPersonalizedAssignmentPerformanceLoading(false);
        setBranchAssignmentReportDetailListLoading(false);
        setContestListLoading(false);
        setSupportAssignmentListLoading(false);
        setPersonalizedAssignmentListLoading(false);
        setMyWrongActivityListLoading(false);
        setActivityReviewListLoading(false);
        return;
      }

      const branchAssignmentDTOList = data.branchAssignmentDTOList || [];
      setBranchAssignmentDTOList(branchAssignmentDTOList);
      setBranchAssignmentListLoading(false);

      // The three former App.tsx boot fetches, hydrated from the same payload (same post-processing
      // as the standalone request path — personalized assignments sorted newest-first).
      setPersonalizedAssignmentDTOList(
        (data.personalizedAssignmentDTOList || []).sort((a, b) => b.id - a.id),
      );
      setPersonalizedAssignmentListLoading(false);
      setMyWrongActivityDTOList(data.wrongActivityDTOList || []);
      setMyWrongActivityListLoading(false);
      setActivityReviewDTOList(data.activityReviewDTOList || []);
      setActivityReviewListLoading(false);

      // Contests + active support assignments from the same bootstrap payload (sort support assignments newest-first
      // to match the old standalone fetch).
      setContestDTOList(data.contestDTOList || []);
      setContestListLoading(false);
      setSupportAssignmentDTOList(
        Array.isArray(data.supportAssignmentDTOList)
          ? (data.supportAssignmentDTOList as any[]).slice().sort((a, b) => b.id - a.id)
          : [],
      );
      setSupportAssignmentListLoading(false);

      // The mastery line rides with the performance it judges (backend MasteryConstants), so there is
      // no extra request; readers go through getMasteryThreshold(), which keeps the 70 fallback until here.
      setMasteryThreshold(data?.unifiedLearnerPerformance?.masteryThreshold);

      const branchAssignmentItems =
        data?.unifiedLearnerPerformance?.internalPerformance
          ?.branchAssignmentItems || [];
      const personalizedAssignmentItems =
        data?.unifiedLearnerPerformance?.internalPerformance
          ?.personalizedAssignmentItems || [];
      const handwrittenExamItems =
        data?.unifiedLearnerPerformance?.externalPerformance
          ?.handwrittenExamItems || [];
      // Destek ödevi results, season-scoped and visibility-gated server-side: a destek whose results the
      // teacher has not published for this şube arrives with resultsPublished=false and no score
      // (never a 0), so the report can say "not published yet" instead of drawing a fail.
      // (Optical exams are retired — the backend carries no opticalExamItems, so nothing reads them.)
      const supportAssignmentItems =
        data?.unifiedLearnerPerformance?.internalPerformance
          ?.supportAssignmentItems || [];
      // Deneme sınavı (mock exam) results. The backend has shipped these in the same payload
      // for a while and this app simply dropped them, so a learner whose school imported a publisher's
      // trial exam saw nothing of it in their own report while the staff console showed it.
      // createdAt is normalised to the date the paper was SAT right here, at ingest, so the table,
      // the row order and the chart all inherit one date instead of each remembering to ask. The
      // import date is kept only for exams that predate examDate. Without this the same exam
      // charted in March and listed as the day the school did the paperwork.
      const mockExamItems = (
        data?.unifiedLearnerPerformance?.externalPerformance
          ?.mockExamItems || []
      ).map((item) => ({
        ...item,
        createdAt: item.examDate ?? item.createdAt,
      }));
      setBranchAssignmentPerformance(branchAssignmentItems);
      setPersonalizedAssignmentPerformance(personalizedAssignmentItems);
      setLearnerHandwrittenExamPerformance(handwrittenExamItems);
      setLearnerSupportAssignmentPerformance(supportAssignmentItems);
      setLearnerMockExamPerformance(mockExamItems);

      const categoryVersionCodeOptionList =
        retrieveCategoryVersionCodeOptionList(userProfileGrade.curriculumType);
      // Do NOT level-filter the REPORT category list. A learner can be assessed on
      // activities tagged with an off-level curriculum version — e.g. a grade-7
      // student holding MEB-IO-MAT-2018 outcomes from before that grade moved to the
      // 2024 curriculum, a code whose availableLevelList is now [4, 8]. Level-filtering
      // drops that version code, so every category it owns is
      // missing from localCategoryDTOList and the student's answered outcomes silently
      // vanish from the report (the admin report doesn't level-filter — hence the
      // mismatch). Extra version codes are harmless here: only categories an activity
      // actually references become outcome nodes.
      const filteredCategoryVersionCodeOptionList =
        filterCategoryVersionCodeOptionList(
          categoryVersionCodeOptionList,
          "MANAGEMENT",
        );
      const versionCodeList = filteredCategoryVersionCodeOptionList.map(
        (categoryVersionCode) => categoryVersionCode.value,
      );
      const sourceList =
        userProfileGrade.curriculumType === "AP"
          ? categoryVersionCodeAPList
          : userProfileGrade.curriculumType === "IB"
          ? categoryVersionCodeIBList
          : userProfileGrade.curriculumType === "DFE"
          ? categoryVersionCodeDFEList
          : categoryVersionCodeMEBList;
      const localCategoryDTOList = sourceList.filter((item) =>
        versionCodeList.includes(item.versionCode),
      );
      // Published for the report surfaces that need to name a category the learner was assessed on
      // but whose own grade differs — ön koşul outcome, which normally sit BELOW the learner's
      // grade. The level-filtered context `categoryDTOList` cannot name those.
      setReportCategoryDTOList(localCategoryDTOList);

      // PO success-% rule, every kind: score ÷ ALL questions, a blank (or, for bireysel/destek, a
      // never-answered question — both padded as `empty` score-0 rows) counts 0. So the şube, bireysel,
      // yazılı and destek outcome trees keep their blank rows. Deneme rows are already per question.
      const branchAssignmentDetails = branchAssignmentItems
        .filter(
          (item) =>
            item.activityScoreDetailDTOList &&
            item.activityScoreDetailDTOList.length > 0,
        )
        .map((item) =>
          buildReportDetailDTOFromPerformance(item, localCategoryDTOList, {
            countBlankAnswers: true,
          }),
        );
      setBranchAssignmentReportDetailDTOList(branchAssignmentDetails);

      setLearnerCompletionOutcomeReportMap(
        buildOutcomeReportMapByCoterie(
          personalizedAssignmentItems,
          localCategoryDTOList,
          { countBlankAnswers: true },
        ),
      );
      setLearnerHandwrittenOutcomeReportMap(
        buildOutcomeReportMapByCoterie(
          handwrittenExamItems,
          localCategoryDTOList,
          { countBlankAnswers: true },
        ),
      );
      setLearnerMockExamOutcomeReportMap(
        buildOutcomeReportMapByCoterie(
          mockExamItems,
          localCategoryDTOList,
        ),
      );
      // Withheld destek (isEvaluated=false) are skipped by the builder, so unpublished results never
      // reach an outcome node.
      setLearnerSupportAssignmentOutcomeReportMap(
        buildOutcomeReportMapByCoterie(
          supportAssignmentItems,
          localCategoryDTOList,
          { countBlankAnswers: true },
        ),
      );

      setBranchAssignmentPerformanceLoading(false);
      setLearnerHandwrittenExamPerformanceLoading(false);
      setLearnerMockExamPerformanceLoading(false);
      setLearnerSupportAssignmentPerformanceLoading(false);
      setPersonalizedAssignmentPerformanceLoading(false);
      setBranchAssignmentReportDetailListLoading(false);
    };
    const retrieveAndSetStandardExamApplicationDTOList = async () => {
      // setStandardExamApplicationListLoading(true);
      // const { data, err } =
      //   await queryStandardExamApplicationListBySeasonIdAndGradeIdAndBranchIdAndCurriculumType(
      //     {
      //       userId: userInformation.id,
      //       userProfileId: userProfile.id,
      //       seasonId: userProfileSeason.id,
      //       gradeId: userProfileGrade.id,
      //       branchId: userProfileBranch.id,
      //       curriculumType: userProfileGrade.curriculumType,
      //       deleted: false,
      //     }
      //   );
      // if (err) {
      //   console.log(err);
      // } else {
      //   setStandardExamApplicationDTOList(
      //     data.sort((a, b) => b.id - a.id)
      //   );
      // }
      setStandardExamApplicationListLoading(false);
    };
    const retrieveAndSetAnnouncementDTOList = async () => {
      setAnnouncementListLoading(true);
      // Single combined read: list + unread count used to be two serial requests
      // with identical params.
      const { data, err } = await queryLearnerAnnouncementFeed({
        userId: userInformation.id,
        userProfileId: userProfile.id,
        brandId: userProfileSeason.institutionDTO.campusDTO.brandDTO.id,
        campusId: userProfileSeason.institutionDTO.campusDTO.id,
        institutionId: userProfileSeason.institutionDTO.id,
        seasonId: userProfileSeason.id,
        gradeId: userProfileGrade.id,
        branchIdList: [userProfileBranch.id],
        includeCorporate: true,
      });
      if (err) {
        return;
      }
      setAnnouncementDTOList(
        data.announcementDTOList.sort((a, b) => b.id - a.id),
      );
      setNumberOfUnreadAnnouncements(data.numberOfUnread);
      setAnnouncementListLoading(false);
    };
    const retrieveAndSetActivityCategoryDTOList = async () => {
      const categoryVersionCodeOptionList =
        retrieveCategoryVersionCodeOptionList(userProfileGrade.curriculumType);
      const filteredCategoryVersionCodeOptionList =
        filterCategoryVersionCodeOptionList(
          categoryVersionCodeOptionList,
          "MANAGEMENT",
          userProfileGrade.level,
        );
      const versionCodeList = filteredCategoryVersionCodeOptionList.map(
        (categoryVersionCode) => categoryVersionCode.value,
      );
      const sourceList =
        userProfileGrade.curriculumType === "AP"
          ? categoryVersionCodeAPList
          : userProfileGrade.curriculumType === "IB"
          ? categoryVersionCodeIBList
          : userProfileGrade.curriculumType === "DFE"
          ? categoryVersionCodeDFEList
          : categoryVersionCodeMEBList;
      const data = sourceList.filter((item) =>
        versionCodeList.includes(item.versionCode),
      );
      setCategoryDTOList(data);
      setCategoryDTOListAsTree(constructCategoryTreeFromParentCode(data));
    };
    const retrieveAndSetSkillDTOList = () => {
      const skillVersionCodeOptionList = retrieveSkillVersionCodeOptionList(
        userProfileGrade.curriculumType,
      );
      const versionCodeList = skillVersionCodeOptionList.map(
        (skillVersionCode) => skillVersionCode.value,
      );
      const data = (skillVersionCodeMEBList as any[])
        .concat(skillVersionCodeIBList as any[])
        .filter((item) => versionCodeList.includes(item.versionCode));
      setSkillDTOList(data);
      setSkillDTOListAsTree(constructCategoryTreeFromParentCode(data));
    };
    const retrieveAndSetAvailableFrameworkAnnualPlanList = async () => {
      setAvailableFrameworkAnnualPlanListLoading(true);
      // /query/dto (not /query/dto/by-params): with no coterieType the backend returns the
      // institution's own plans UNIONED with the source brand's predefined masters for the
      // coteries it doesn't own — `level` is what resolves the masters. Without this, a
      // predefined-only institution's learner never gets a coterie to pick (the pickers are
      // plan-driven), so the per-coterie fallback stays unreachable.
      const { data, err } = await queryLearnerFrameworkAnnualPlanListByParams({
        brandId: userProfileSeason.institutionDTO.campusDTO.brandDTO.id,
        campusId: userProfileSeason.institutionDTO.campusDTO.id,
        institutionId: userProfileSeason.institutionDTO.id,
        seasonId: userProfileSeason.id,
        gradeId: userProfileGrade.id,
        curriculumType: userProfileGrade.curriculumType,
        level: userProfileGrade.level,
      });
      if (err) {
        setAvailableFrameworkAnnualPlanList([]);
        setAvailableFrameworkAnnualPlanListLoading(false);
        return;
      }
      setAvailableFrameworkAnnualPlanList(data || []);
      setAvailableFrameworkAnnualPlanListLoading(false);
    };
    // Contests + active support assignments are now bundled into retrieveAndSetLearnerCatchtivityBootstrap
    // (one catchtivity request instead of two extra parallel ones) — see the contestParams/supportAssignmentParams
    // it sends and the setContestDTOList/setSupportAssignmentDTOList it performs from the shared payload.

    if (boarding === "DASHBOARD") {
      const { pathname } = window.location;
      if (pathname.startsWith("/upload/file-to-application")) return;
      if (pathname.startsWith("/solve/activity")) return;
      retrieveAndSetLearnerCatchtivityBootstrap();
      retrieveAndSetStandardExamApplicationDTOList();
      retrieveAndSetAvailableFrameworkAnnualPlanList();
      retrieveAndSetAnnouncementDTOList();
      retrieveAndSetLearnerGamificationBootstrap();
      retrieveAndSetActivityCategoryDTOList();
      retrieveAndSetSkillDTOList();
    }
  }, [boarding, learnerDataRefreshCounter]);

  useEffect(() => {
    const contentList = [
      ...branchAssignmentDTOList.map((item) => ({
        ...item,
        type: "BRANCH_ASSIGNMENT",
      })),
      ...standardExamApplicationDTOList.map((item) => ({
        ...item,
        type: "STANDARD_EXAM",
      })),
      // Destek ödevleri are a fourth KIND in this one list, not a parallel "Çalışmalar"
      // surface — the nav badge, the merged /homeworks list, the dashboard card and the
      // calendar all read them from here. normalizeSupportAssignmentContent projects the
      // group payload onto this shape WITHOUT inventing a beginAt/endAt.
      ...supportAssignmentDTOList.map(normalizeSupportAssignmentContent),
    ];
    const currentTimestamp = new Date().getTime();
    const corporatePersonalizedActive = personalizedAssignmentDTOList
      .filter(
        (pa: any) =>
          isStaffSentPersonalizedAssignment(pa.triggerType) &&
          pa.completionStatus !== "COMPLETED",
      )
      .map((pa: any) => {
        // BRANCH_ASSIGNMENT borrows the parent şube ödevi's name; STAFF_DIRECT hangs on no parent
        // (triggerId is the -1 sentinel), so its ders — carried on the row — names the card.
        const parent =
          pa.triggerType === "BRANCH_ASSIGNMENT"
            ? branchAssignmentDTOList.find((ca: any) => ca.id === pa.triggerId)
            : null;
        return {
          ...pa,
          type: "PERSONALIZED_ASSIGNMENT",
          // The homework rows colour and label by coterie: a STAFF_DIRECT row carries its own, a
          // şube-ödevi-hung one borrows the parent's. No name is synthesized for a parentless
          // round — both card surfaces name it by kind once they see the STAFF_DIRECT trigger,
          // so stamping its ders here would only be discarded.
          coterieType: pa.coterieType ?? parent?.coterieType,
          subjectDescriptionDTO:
            pa.subjectDescriptionDTO ??
            (parent
              ? { name: parent.name, description: parent.description }
              : undefined),
        };
      });
    // "Already open?" — hoisted out of the comparator so it is allocated once per
    // recompute instead of once per comparison.
    //
    // A destek ödevi need not carry a `beginAt`, and for THAT kind a missing one means
    // ALREADY OPEN — otherwise an actionable group would sort below the branch rows on
    // a missing field. The concession is scoped to SUPPORT_ASSIGNMENT on purpose: every
    // other kind keeps the pre-existing comparison, so no branch/exam ordering moves.
    const isOpen = (content: any) =>
      content.type === "SUPPORT_ASSIGNMENT" && content.beginAt == null
        ? true
        : content.beginAt <= currentTimestamp;
    setActiveContentList([
      ...contentList
        // NULL-TOLERANCE IS SCOPED TO SUPPORT_ASSIGNMENT. A destek ödevi legitimately carries
        // NO deadline, and `endAt > currentTimestamp` is `false` for a null — such a row
        // would silently VANISH from every merged surface. But `endAt` is OPTIONAL on
        // IBranchAssignment too, so tolerating a null for every kind would make a branch
        // assignment / standard-exam application with no end date active FOREVER: it
        // would count into the nav badge, hold a dashboard row and a /homeworks row, and
        // never expire. Those kinds keep the strict comparison they always had.
        .filter(
          (content) =>
            (content.endAt != null
              ? content.endAt > currentTimestamp
              : content.type === "SUPPORT_ASSIGNMENT") && !content.isEvaluated,
        )
        .sort((a, b) => {
          const aActive = isOpen(a) && a.completionStatus !== "COMPLETED";
          const bActive = isOpen(b) && b.completionStatus !== "COMPLETED";

          if (aActive && !bActive) return -1;
          if (!aActive && bActive) return 1;

          // Soonest deadline first, no-deadline rows last. The id tiebreak keeps the
          // order stable for two null-endAt rows (Infinity - Infinity is NaN, which
          // leaves Array.prototype.sort free to shuffle them on every render).
          return (
            (a.endAt ?? Infinity) - (b.endAt ?? Infinity) || a.id - b.id
          );
        }),
      ...corporatePersonalizedActive,
    ]);
    // passiveContentList (expired + evaluated homeworks) is NO LONGER derived here: the bootstrap now
    // fetches only ACTIVE assignments, so this list would always be empty. The previous/passive set is
    // fetched lazily by /practise (PractiseNowScreen) — the only screen that renders it — so a learner
    // who never opens /practise never pays to load every homework since season start.
  }, [
    branchAssignmentDTOList,
    supportAssignmentDTOList,
    standardExamApplicationDTOList,
    personalizedAssignmentDTOList,
  ]);

  useEffect(() => {
    const personalizedAssignmentList = [...personalizedAssignmentDTOList];
    setActivePersonalizedAssignmentList(
      personalizedAssignmentList.filter(
        (work) => work.completionStatus !== "COMPLETED",
      ),
    );
    setPassivePersonalizedAssignmentList(
      personalizedAssignmentList.filter(
        (work) => work.completionStatus === "COMPLETED",
      ),
    );
  }, [personalizedAssignmentDTOList]);

  useEffect(() => {
    const constructCategoryScoreMapFromBranchAssignmentReportDetailList = (
      branchAssignmentReportList,
    ) => {
      const branchAssignmentCategoryScoreList = [];
      const recursivelySetCategoryScoreMap = (
        branchAssignmentReportList,
      ) => {
        branchAssignmentReportList.forEach((categoryActivityAnswerDTO) => {
          const {
            subCategoryActivityAnswerDTOList,
            categoryDTO,
            activityScoreDTOSet,
          } = categoryActivityAnswerDTO;
          if (
            subCategoryActivityAnswerDTOList &&
            subCategoryActivityAnswerDTOList.length === 0
          ) {
            const activityCount = activityScoreDTOSet?.length ?? 0;
            if (activityCount > 0) {
              const totalScore = activityScoreDTOSet.reduce(
                (sum, activity) => sum + activity.score,
                0,
              );

              const foundBranchAssignmentCategoryScore =
                branchAssignmentCategoryScoreList.find(
                  (branchAssignmentCategoryScore) =>
                    branchAssignmentCategoryScore.categoryId ===
                    categoryDTO.id,
                );
              if (foundBranchAssignmentCategoryScore) {
                foundBranchAssignmentCategoryScore.totalScore += totalScore;
                foundBranchAssignmentCategoryScore.activityCount +=
                  activityCount;
              } else {
                branchAssignmentCategoryScoreList.push({
                  categoryId: categoryDTO.id,
                  categoryName: categoryDTO.name,
                  categoryCode: categoryDTO.code,
                  versionCode: categoryDTO.versionCode,
                  totalScore: totalScore,
                  activityCount,
                });
              }
            }
          }

          if (subCategoryActivityAnswerDTOList) {
            recursivelySetCategoryScoreMap(subCategoryActivityAnswerDTOList);
          }
        });
      };
      recursivelySetCategoryScoreMap(branchAssignmentReportList);
      setBranchAssignmentCategoryScoreList(
        branchAssignmentCategoryScoreList,
      );
    };

    if (!branchAssignmentReportDetailDTOList) return;
    if (branchAssignmentReportDetailDTOList.length === 0) return;
    const convertedBranchAssignmentReportDetailList =
      branchAssignmentReportDetailDTOList.map(
        (branchAssignmentReportDetail) => ({
          ...branchAssignmentReportDetail,
          subCategoryActivityAnswerDTOList:
            branchAssignmentReportDetail.categoryActivityAnswerDTOList,
        }),
      );

    constructCategoryScoreMapFromBranchAssignmentReportDetailList(
      convertedBranchAssignmentReportDetailList,
    );
  }, [branchAssignmentReportDetailDTOList]);

  useEffect(() => {
    // Build the per-coterie outcome map from the framework annual plans, restricted to
    // weeks that have already started (week.beginAt <= now() — covers the current ongoing
    // week plus all previous weeks of the school year). This drives both the LearningPath
    // outcome filter and the subject-description fetch below.
    const buildOutcomeMap = () => {
      const map = {};
      const now = Date.now();
      for (const plan of availableFrameworkAnnualPlanList || []) {
        const { coterieType, frameworkAnnualPlanWeekDTOList } = plan || {};
        if (!coterieType) continue;
        if (!map[coterieType]) map[coterieType] = [];
        const list = map[coterieType];
        for (const week of frameworkAnnualPlanWeekDTOList || []) {
          if (week?.beginAt == null) continue;
          if (week.beginAt > now) continue;
          for (const cat of week.frameworkAnnualPlanWeekCategoryDTOList || []) {
            if (cat?.categoryId == null) continue;
            if (!list.includes(cat.categoryId)) list.push(cat.categoryId);
          }
        }
      }
      return map;
    };

    const retrieveAndSetSubjectDescriptionDTOList = async (categoryIdList) => {
      setSubjectDescriptionListLoading(true);
      if (categoryIdList.length === 0) {
        setSubjectDescriptionDTOList([]);
        setSubjectDescriptionListLoading(false);
        return;
      }
      const { data, err } = await querySubjectDescriptionListByCategoryIdList({
        categoryIdList,
      });
      if (err) {
        setSubjectDescriptionListLoading(false);
        return;
      }
      setSubjectDescriptionDTOList(data);
      setSubjectDescriptionListLoading(false);
    };

    const map = buildOutcomeMap();
    setFrameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap(map);

    // Flatten the map's category ids to one deduped list for the subject-description fetch.
    const flatCategoryIdSet = new Set();
    (Object.values(map) as unknown[][]).forEach((ids) => ids.forEach((id) => flatCategoryIdSet.add(id)));
    retrieveAndSetSubjectDescriptionDTOList(Array.from(flatCategoryIdSet));
  }, [availableFrameworkAnnualPlanList]);

  useEffect(() => {
    const retrieveAndInitializeTaskItemProgressDTO = async (
      taskItemProgressDTO,
    ) => {
      setTaskItemProgressListLoading(true);
      const { data, err } = await initializeTaskItemProgressDTO({
        level: taskItemProgressDTO.level,
        beginAt: taskItemProgressDTO.beginAt,
        endAt: taskItemProgressDTO.endAt,
        userId: userInformation.id,
        userProfileId: userProfile.id,
      });
      if (err) {
        return;
      }
      setTaskItemProgressDTOList(data);
      setTaskItemProgressListLoading(false);
    };
    if (
      taskItemProgressDTOList &&
      taskItemProgressDTOList.length === 0
    )
      return;
    const taskItemProgressDTO = taskItemProgressDTOList[0];
    const { taskItemDTOList } = taskItemProgressDTO;
    let isInitialized = true;
    for (const taskItemDTO of taskItemDTOList) {
      const { taskItemProgressDTO } = taskItemDTO;
      if (!taskItemProgressDTO) {
        isInitialized = false;
        break;
      }
    }
    if (!isInitialized) {
      retrieveAndInitializeTaskItemProgressDTO(
        taskItemProgressDTO,
      );
    }
  }, [taskItemProgressDTOList]);

  useEffect(() => {
    if (!webSocketStompClient || !userProfile) return;
    const subscribed = webSocketStompClient.subscribe(
      `/${userProfile.id}/gamification/update-points`,
      (message) => {
        retrieveAndSetLearnerPointsStatus();
      },
    );
    return () => {
      subscribed.unsubscribe();
    };
  }, [webSocketStompClient, userProfile]);

  useEffect(() => {
    if (!webSocketStompClient || !userProfile) return;
    const subscribed = webSocketStompClient.subscribe(
      `/${userProfile.id}/gamification/update-task`,
      (message) => {
        retrieveAndSetTaskItemProgressDTOList();
      },
    );
    return () => {
      subscribed.unsubscribe();
    };
  }, [webSocketStompClient, userProfile]);

  // Single-call learner bootstrap. Replaces the four separate gamification fetches the
  // boarding effect used to fan out (personal status, this-week ranking, all-time ranking,
  // corporate task progress) plus App.js's earned-achievement fetch. The websocket handlers below
  // still use the per-payload fetchers because they only need to refresh a subset.
  const retrieveAndSetLearnerGamificationBootstrap = async () => {
    // Load the achievement-rule catalog (global/static reference data) once so achievement progress bars
    // and the new-achievement popup draw against the REAL backend thresholds. Fire-and-forget; the
    // achievement screens also gate on it as a safety net.
    if (!hasAchievementRuleCatalog()) {
      retrieveAchievementCatalog().then(({ data, err }) => {
        if (!err && data) setAchievementRuleCatalog(data as any[]);
      });
    }
    setEarnedAchievementListLoading(true);
    setThisWeekRankingListLoading(true);
    setAllTimeRankingListLoading(true);
    setTaskItemProgressListLoading(true);
    // Market purchases + mascot inventory ride along (was two separate App.tsx boot requests).
    setMarketItemPurchaseListLoading(true);
    setMascotInventoryItemListLoading(true);
    const { data, err } = await retrieveLearnerGamificationBootstrap({
      userId: userInformation.id,
      userProfileId: userProfile.id,
      seasonId: userProfileSeason.id,
      seasonBeginAt: userProfileSeason.beginAt,
      weekBeginAt: calculateStartDateOfWeek(new Date(), true).getTime(),
      weekEndAt: calculateEndDateOfWeek(new Date(), true).getTime(),
      level: userProfileGrade.level,
      // Bounded leaderboard: top-50 + own row + server-computed branch/grade aggregates instead of
      // every learner in the season (the old full lists were the dominant payload cost).
      rankingLimit: RANKING_LIMIT,
      includeMarketItemPurchases: true,
      includeMascotInventoryItems: true,
    });
    if (err) {
      setEarnedAchievementListLoading(false);
      setThisWeekRankingListLoading(false);
      setAllTimeRankingListLoading(false);
      setTaskItemProgressListLoading(false);
      setMarketItemPurchaseListLoading(false);
      setMascotInventoryItemListLoading(false);
      return;
    }
    setPersonalStatus(data.personalStatus);
    setWeeklyStreak(
      data.weeklyStreak || {
        activeDaysOfWeek: [],
        currentStreakDays: 0,
        todayActive: false,
        weeklyActivityCount: 0,
      },
    );
    setNextAchievementProgress(data.nextAchievementProgress || null);
    setSummedActionCounts(data.summedActionCounts || []);
    setLearnerGoalProgressList(data.learnerGoalProgressDTOList || []);
    setEarnedAchievementDTOList(data.earnedAchievementDTOList || []);
    setThisWeekRankingDTOList(data.thisWeekRankingDTOList || []);
    setAllTimeRankingDTOList(data.allTimeRankingDTOList || []);
    setSelfThisWeekRanking(data.selfThisWeekRanking || null);
    setSelfAllTimeRanking(data.selfAllTimeRanking || null);
    setThisWeekBranchRankingDTOList(data.thisWeekBranchRankingDTOList || []);
    setAllTimeBranchRankingDTOList(data.allTimeBranchRankingDTOList || []);
    setTaskItemProgressDTOList(
      data.taskItemProgressDTOList || [],
    );
    setMarketItemPurchaseDTOList(data.marketItemPurchaseDTOList || []);
    setMascotInventoryItemDTOList(data.mascotInventoryItemDTOList || []);
    setEarnedAchievementListLoading(false);
    setThisWeekRankingListLoading(false);
    setAllTimeRankingListLoading(false);
    setTaskItemProgressListLoading(false);
    setMarketItemPurchaseListLoading(false);
    setMascotInventoryItemListLoading(false);
  };

  // One mini-aggregate request per "update-points" websocket event (was three: personal status +
  // this-week ranking + all-time ranking). Same bounded-ranking semantics as the bootstrap.
  const retrieveAndSetLearnerPointsStatus = async () => {
    setThisWeekRankingListLoading(true);
    setAllTimeRankingListLoading(true);
    const { data, err } = await retrieveLearnerPointsStatus({
      userId: userInformation.id,
      userProfileId: userProfile.id,
      seasonId: userProfileSeason.id,
      seasonBeginAt: userProfileSeason.beginAt,
      weekBeginAt: calculateStartDateOfWeek(new Date(), true).getTime(),
      weekEndAt: calculateEndDateOfWeek(new Date(), true).getTime(),
      rankingLimit: RANKING_LIMIT,
    });
    if (err) {
      setThisWeekRankingListLoading(false);
      setAllTimeRankingListLoading(false);
      return;
    }
    setPersonalStatus(data.personalStatus);
    setThisWeekRankingDTOList(data.thisWeekRankingDTOList || []);
    setAllTimeRankingDTOList(data.allTimeRankingDTOList || []);
    setSelfThisWeekRanking(data.selfThisWeekRanking || null);
    setSelfAllTimeRanking(data.selfAllTimeRanking || null);
    setThisWeekBranchRankingDTOList(data.thisWeekBranchRankingDTOList || []);
    setAllTimeBranchRankingDTOList(data.allTimeBranchRankingDTOList || []);
    setThisWeekRankingListLoading(false);
    setAllTimeRankingListLoading(false);
  };

  const retrieveAndSetTaskItemProgressDTOList = async () => {
    setTaskItemProgressListLoading(true);
    const { data, err } = await queryTaskItemProgressDTOListByParams({
      beginAt: calculateStartDateOfWeek(new Date(), true).getTime(),
      endAt: calculateEndDateOfWeek(new Date(), true).getTime(),
      userId: userInformation.id,
      userProfileId: userProfile.id,
      level: userProfileGrade.level,
    });
    if (err) {
      return;
    }
    setTaskItemProgressDTOList(data);
    setTaskItemProgressListLoading(false);
  };

  return (
    <div className="xap-fill">
      <TaskProgressIndicator
        loading={false}
        taskItemProgressDTOList={taskItemProgressDTOList}
        language={language}
      />
      {props.children}
    </div>
  );
};

export default LearnerApp;
